'use client';

import {
  Alert,
  Box,
  Button,
  Container,
  LinearProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { UserAvatar } from 'src/components/ui/UserAvatar';

interface Baller {
  address: string;
  held_since: number;
  amount: number;
  ens_name: string;
  profile: {
    username: string | null;
    bio: string | null;
    avatar_url: string | null;
    twitter_handle: string | null;
    telegram_handle: string | null;
    role: string | null;
    badges: any[];
  };
}

interface SectionBalance {
  section: string;
  amount: number;
}

interface BallerSubmissionResult {
  address: string;
  daysSince: number;
  tokenBalance: number;
  ensName: string;
  eligible: boolean;
  reason?: string;
}

interface Stats {
  totalBalled: number;
  totalBallerCount: number;
  totalBallers: number;
  sections: SectionBalance[];
}

export default function DiamondBallsPage() {
  const [address, setAddress] = useState('');
  const [ballers, setBallers] = useState<Baller[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [sectionBalances, setSectionBalances] = useState<SectionBalance[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  } | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const totalSupply = 1000000000; // 1 billion

  const formatAddress = (address: string): string => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  useEffect(() => {
    // Initial data load
    fetchBallers();
    fetchHolderBalances();
  }, []);

  const fetchBallers = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/diamond-ballers');
      const data = await response.json();
      setBallers(data.ballers || []);
      setStats(data.stats || null);
    } catch (error) {
      console.error('Error fetching diamond ballers:', error);
      showNotification('Failed to load diamond ballers', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchHolderBalances = async () => {
    try {
      const response = await fetch('/api/diamond-ballers/holder-balances');
      const data = await response.json();
      setSectionBalances(data || []);
    } catch (error) {
      console.error('Error fetching holder balances:', error);
    }
  };

  const showNotification = (
    message: string,
    severity: 'success' | 'error' | 'info',
  ) => {
    setNotification({ open: true, message, severity });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!address.trim()) {
      showNotification('Please enter a valid address', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/diamond-ballers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ address: address.trim() }),
      });

      const result: BallerSubmissionResult = await response.json();

      if (result.eligible) {
        showNotification(
          'Congratulations! You are now a Diamond Baller!',
          'success',
        );
        setAddress(''); // Clear the input

        // Refresh the ballers list
        fetchBallers();
        fetchHolderBalances();
      } else {
        showNotification(
          result.reason
            ? `Not eligible: ${result.reason}`
            : `Not eligible: You need to hold for at least 14 days without outgoing transactions. Current days: ${result.daysSince}`,
          'error',
        );
      }
    } catch (error) {
      console.error('Error submitting address:', error);
      showNotification('Error submitting address. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification(null);
  };

  // Calculate section percentages and widths
  const getSectionWidths = () => {
    const sections = {
      burnt: { amount: 0, color: '#B82812' },
      balled: { amount: 0, color: '#D1F2F7' },
      inactive: { amount: 0, color: '#000000' },
      active: { amount: 0, color: '#7DBB43' },
      pool: { amount: 0, color: '#023CBA' },
    };

    // Update with actual values
    sectionBalances.forEach((balance) => {
      if (balance.section in sections) {
        sections[balance.section as keyof typeof sections].amount =
          balance.amount;
      }
    });

    // Convert to percentages
    const percentages = Object.entries(sections).map(([key, value]) => ({
      section: key,
      percentage: (value.amount / totalSupply) * 100,
      color: value.color,
      amount: value.amount,
    }));

    return percentages;
  };

  const sectionWidths = getSectionWidths();

  // Calculate held savagely percentage
  const getHeldSavagelyPercentage = () => {
    const burnt =
      sectionBalances.find((b) => b.section === 'burnt')?.amount || 0;
    const balled =
      sectionBalances.find((b) => b.section === 'balled')?.amount || 0;
    const inactive =
      sectionBalances.find((b) => b.section === 'inactive')?.amount || 0;

    return (((burnt + balled + inactive) / totalSupply) * 100).toFixed(1);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        backgroundImage:
          'linear-gradient(rgba(0, 9, 31, 0.7), rgba(0, 0, 0, 0.8))',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        pt: '100px',
        pb: '50px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* SVG Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.3,
          zIndex: 0,
        }}
      >
        <svg
          className='absolute inset-0 h-full w-full'
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient
              id="grid-gradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.6" />
              <stop offset="50%" stopColor="#8b5cf6" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.6" />
            </linearGradient>
            <radialGradient
              id="orb-gradient"
              cx="50%"
              cy="50%"
              r="50%"
              fx="50%"
              fy="50%"
            >
              <stop offset="0%" stopColor="#60a5fa" stopOpacity="1" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
            </radialGradient>
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="15" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
          </defs>

          <rect x="0" y="0" width="100%" height="100%" fill="#111827" />

          <pattern
            id="grid"
            x="0"
            y="0"
            width="120"
            height="120"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 120 0 L 0 0 0 120"
              fill="none"
              stroke="url(#grid-gradient)"
              strokeWidth="1.2"
            />
            <path
              d="M 0 0 L 120 120"
              fill="none"
              stroke="url(#grid-gradient)"
              strokeWidth="1.2"
              opacity="0.8"
            />
          </pattern>
          <rect x="0" y="0" width="100%" height="100%" fill="url(#grid)" />

          <circle
            cx="25%"
            cy="30%"
            r="12%"
            fill="url(#orb-gradient)"
            filter="url(#glow)"
            opacity="0.5"
          />
          <circle
            cx="75%"
            cy="65%"
            r="15%"
            fill="url(#orb-gradient)"
            filter="url(#glow)"
            opacity="0.6"
          />
          <circle
            cx="80%"
            cy="20%"
            r="10%"
            fill="url(#orb-gradient)"
            filter="url(#glow)"
            opacity="0.4"
          />

          <path
            d="M 25% 30% L 75% 65% L 80% 20% Z"
            fill="none"
            stroke="#6366f1"
            strokeWidth="1.5"
            opacity="0.4"
          />
        </svg>
      </Box>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            textAlign: 'center',
            color: 'white',
            mb: 4,
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            fontWeight="bold"
            mb={2}
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              background: 'linear-gradient(to right, #60a5fa, #3b82f6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
            }}
          >
            Welcome Loyal Diamond Ballers
          </Typography>

          <Typography
            variant="body1"
            mb={3}
            sx={{
              fontSize: { xs: '1rem', sm: '1.1rem' },
              opacity: 0.9,
              color: '#e5e7eb',
            }}
          >
            Enter diamond Ball status by submitting your address below
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              mb: 4,
              mx: 'auto',
              maxWidth: 600,
            }}
          >
            <TextField
              value={address}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setAddress(e.target.value)
              }
              placeholder="Enter your address"
              variant="outlined"
              fullWidth={true}
              sx={{
                backgroundColor: 'rgba(17, 24, 39, 0.7)',
                backdropFilter: 'blur(10px)',
                borderRadius: '8px',
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': {
                    borderColor: '#3b82f6',
                  },
                  '&:hover fieldset': {
                    borderColor: '#60a5fa',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  },
                  '& input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                  },
                },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              sx={{
                background: 'linear-gradient(to right, #3b82f6, #2563eb)',
                '&:hover': {
                  background: 'linear-gradient(to right, #2563eb, #1d4ed8)',
                },
                height: isMobile ? '40px' : '56px',
                width: isMobile ? '100%' : 'auto',
                fontWeight: 'bold',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3)',
                '&:disabled': {
                  background: 'rgba(59, 130, 246, 0.5)',
                },
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          </Box>

          <Box
            mb={4}
            sx={{
              backgroundColor: 'rgba(17, 24, 39, 0.7)',
              backdropFilter: 'blur(10px)',
              borderRadius: '12px',
              padding: '24px',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: 'rgba(59, 130, 246, 0.4)',
                boxShadow: '0 4px 12px -1px rgba(59, 130, 246, 0.2)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            <Typography
              variant="h5"
              component="h2"
              fontWeight="bold"
              mb={2}
              sx={{
                color: '#60a5fa',
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
              }}
            >
              Requirements
            </Typography>
            <Typography variant="body1" mb={1} sx={{ color: '#e5e7eb' }}>
              You must hold 50k Boomer
            </Typography>
            <Typography variant="body1" sx={{ color: '#e5e7eb' }}>
              No outbound transactions in 14 days
            </Typography>
          </Box>

          <Box
            mb={4}
            sx={{
              backgroundColor: 'rgba(17, 24, 39, 0.7)',
              backdropFilter: 'blur(10px)',
              borderRadius: '12px',
              padding: '24px',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: 'rgba(59, 130, 246, 0.4)',
                boxShadow: '0 4px 12px -1px rgba(59, 130, 246, 0.2)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            <Typography
              variant="h5"
              component="h2"
              fontWeight="bold"
              mb={2}
              sx={{
                color: '#60a5fa',
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
              }}
            >
              Big Baller Totals
            </Typography>
            <Typography variant="body1" mb={1} sx={{ color: '#e5e7eb' }}>
              Total Diamond Balled:{' '}
              <span style={{ color: '#60a5fa', fontWeight: 'bold' }}>
                {stats?.totalBalled?.toLocaleString() || '0'}
              </span>
            </Typography>
            <Typography variant="body1" mb={1} sx={{ color: '#e5e7eb' }}>
              Total Ballers:{' '}
              <span style={{ color: '#60a5fa', fontWeight: 'bold' }}>
                {stats?.totalBallerCount?.toLocaleString() || '0'}
              </span>
            </Typography>
            <Typography variant="body1" mb={2} sx={{ color: '#e5e7eb' }}>
              Held Savagely:{' '}
              <span style={{ color: '#60a5fa', fontWeight: 'bold' }}>
                {getHeldSavagelyPercentage()}%
              </span>
            </Typography>
            <Typography
              variant="body2"
              sx={{ opacity: 0.8, fontSize: '0.85rem', color: '#9ca3af' }}
            >
              HODLing = holding for 60+ days and has not submitted their wallet
              for Diamond Ball status
            </Typography>
          </Box>

          {/* Supply Distribution Bar */}
          <Box
            mb={6}
            sx={{
              backgroundColor: 'rgba(17, 24, 39, 0.7)',
              backdropFilter: 'blur(10px)',
              borderRadius: '12px',
              padding: '24px',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: 'rgba(59, 130, 246, 0.4)',
                boxShadow: '0 4px 12px -1px rgba(59, 130, 246, 0.2)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            <Typography
              variant="h5"
              component="h2"
              fontWeight="bold"
              mb={3}
              sx={{
                color: '#60a5fa',
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                textAlign: 'center',
              }}
            >
              Supply Distribution
            </Typography>

            {/* Distribution Bar */}
            <Box
              sx={{
                display: 'flex',
                borderRadius: '20px',
                overflow: 'hidden',
                height: isMobile ? 'auto' : '80px',
                flexDirection: isMobile ? 'column' : 'row',
                width: '100%',
                maxWidth: '820px',
                mx: 'auto',
                mb: 3,
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                position: 'relative',
              }}
            >
              {sectionWidths.map(
                (section, index) =>
                  section.percentage > 0 && (
                    <Tooltip
                      key={section.section}
                      title={
                        <Box sx={{ textAlign: 'center', p: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 'bold', color: '#fff' }}
                          >
                            {section.section.charAt(0).toUpperCase() +
                              section.section.slice(1)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#e5e7eb' }}>
                            {section.percentage.toFixed(1)}%
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: '#9ca3af' }}
                          >
                            {section.amount.toLocaleString()} BOOMER
                          </Typography>
                        </Box>
                      }
                    >
                      <Box
                        sx={{
                          width: isMobile ? '100%' : `${section.percentage}%`,
                          height: isMobile ? '40px' : '100%',
                          bgcolor: section.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.8rem',
                          px: 1,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          position: 'relative',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.02)',
                            zIndex: 1,
                          },
                          borderRadius: isMobile
                            ? index === 0
                              ? '20px 20px 0 0'
                              : index === sectionWidths.length - 1
                                ? '0 0 20px 20px'
                                : 0
                            : index === 0
                              ? '20px 0 0 20px'
                              : index === sectionWidths.length - 1
                                ? '0 20px 20px 0'
                                : 0,
                        }}
                      >
                        {section.percentage > 5 && (
                          <Box
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background:
                                'linear-gradient(to bottom, rgba(255,255,255,0.1), rgba(255,255,255,0))',
                              opacity: 0.8,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                fontWeight: 'bold',
                                textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                              }}
                            >
                              {section.percentage.toFixed(1)}%
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Tooltip>
                  ),
              )}
            </Box>

            {/* Legend */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(3, 1fr)',
                  md: 'repeat(5, 1fr)',
                },
                gap: 2,
                mt: 2,
              }}
            >
              {sectionWidths.map(
                (section) =>
                  section.percentage > 0 && (
                    <Box
                      key={`legend-${section.section}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid rgba(59, 130, 246, 0.2)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(59, 130, 246, 0.15)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.2)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          bgcolor: section.color,
                          borderRadius: '4px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          position: 'relative',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            inset: 0,
                            borderRadius: '4px',
                            background:
                              'linear-gradient(45deg, rgba(255,255,255,0.2), rgba(255,255,255,0))',
                            opacity: 0.5,
                          },
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: '#e5e7eb',
                            fontWeight: 'bold',
                            fontSize: '0.875rem',
                          }}
                        >
                          {section.section.charAt(0).toUpperCase() +
                            section.section.slice(1)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#9ca3af',
                            display: 'block',
                          }}
                        >
                          {section.percentage.toFixed(1)}%
                        </Typography>
                      </Box>
                    </Box>
                  ),
              )}
            </Box>
          </Box>

          {/* Leaderboard */}
          <Paper
            elevation={3}
            sx={{
              borderRadius: '12px',
              overflow: 'hidden',
              bgcolor: 'rgba(17, 24, 39, 0.7)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: 'rgba(59, 130, 246, 0.4)',
                boxShadow: '0 4px 12px -1px rgba(59, 130, 246, 0.2)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            {isLoading ? (
              <LinearProgress sx={{ height: 4 }} />
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow
                        sx={{
                          bgcolor: 'rgba(59, 130, 246, 0.1)',
                        }}
                      >
                        <TableCell
                          align="center"
                          sx={{
                            color: '#60a5fa',
                            fontWeight: 'bold',
                            width: '70px',
                            borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
                          }}
                        >
                          Rank
                        </TableCell>
                        <TableCell
                          sx={{
                            color: '#60a5fa',
                            fontWeight: 'bold',
                            maxWidth: '300px',
                            borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
                          }}
                        >
                          Profile
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            color: '#60a5fa',
                            fontWeight: 'bold',
                            width: '100px',
                            borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
                          }}
                        >
                          Days
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            color: '#60a5fa',
                            fontWeight: 'bold',
                            width: '100px',
                            borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
                          }}
                        >
                          Amount
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ballers.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            align="center"
                            sx={{ color: '#e5e7eb' }}
                          >
                            No diamond ballers found. Be the first to join!
                          </TableCell>
                        </TableRow>
                      ) : (
                        [...ballers]
                          .sort((a, b) => {
                            // First sort by whether they have a profile
                            if (a.profile.username && !b.profile.username) {
                              return -1;
                            }
                            if (!a.profile.username && b.profile.username) {
                              return 1;
                            }
                            // Then sort by days held
                            return b.held_since - a.held_since;
                          })
                          .map((baller, index) => (
                            <TableRow
                              key={`${baller.address}-${index}`}
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'rgba(59, 130, 246, 0.05)',
                                  cursor: 'pointer',
                                },
                                borderBottom:
                                  '1px solid rgba(59, 130, 246, 0.1)',
                                transition: 'all 0.2s ease',
                              }}
                              onClick={() => {
                                if (baller.profile.username) {
                                  window.location.href = `/social/members/${baller.profile.username}`;
                                } else {
                                  window.open(
                                    `https://basescan.org/address/${baller.address}`,
                                    '_blank',
                                  );
                                }
                              }}
                            >
                              <TableCell
                                align="center"
                                sx={{
                                  fontWeight: 'bold',
                                  color: '#60a5fa',
                                }}
                              >
                                {index + 1}
                              </TableCell>
                              <TableCell
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: isMobile ? '120px' : '300px',
                                  color: '#e5e7eb',
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                  }}
                                >
                                  <UserAvatar
                                    url={baller.profile.avatar_url || undefined}
                                    username={
                                      baller.profile.username || baller.address
                                    }
                                    size="sm"
                                    badgeCount={
                                      baller.profile.badges?.length || 0
                                    }
                                  />
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontWeight: 'bold',
                                        color: '#e5e7eb',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {baller.profile.username ||
                                        formatAddress(baller.address)}
                                    </Typography>
                                    {baller.profile.role && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color:
                                            baller.profile.role === 'Whale'
                                              ? '#60a5fa'
                                              : baller.profile.role === 'Mod'
                                                ? '#4ade80'
                                                : baller.profile.role === 'Dev'
                                                  ? '#a78bfa'
                                                  : '#9ca3af',
                                        }}
                                      >
                                        {baller.profile.role}
                                      </Typography>
                                    )}
                                    {baller.profile.badges &&
                                      baller.profile.badges.length > 0 && (
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            gap: 0.5,
                                            mt: 0.5,
                                          }}
                                        >
                                          {baller.profile.badges.map(
                                            (badge) => (
                                              <Tooltip
                                                key={badge.id}
                                                title={
                                                  <Box
                                                    sx={{
                                                      textAlign: 'center',
                                                      p: 1,
                                                    }}
                                                  >
                                                    <Typography
                                                      variant="subtitle2"
                                                      sx={{
                                                        fontWeight: 'bold',
                                                        color: '#fff',
                                                      }}
                                                    >
                                                      {badge.name}
                                                    </Typography>
                                                    {badge.description && (
                                                      <Typography
                                                        variant="caption"
                                                        sx={{
                                                          color: '#9ca3af',
                                                        }}
                                                      >
                                                        {badge.description}
                                                      </Typography>
                                                    )}
                                                  </Box>
                                                }
                                              >
                                                <Box
                                                  sx={{
                                                    width: 16,
                                                    height: 16,
                                                    borderRadius: '50%',
                                                    overflow: 'hidden',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor:
                                                      'rgba(59, 130, 246, 0.1)',
                                                    border:
                                                      '1px solid rgba(59, 130, 246, 0.2)',
                                                    cursor: 'help',
                                                  }}
                                                >
                                                  {badge.icon_url ? (
                                                    <img
                                                      src={badge.icon_url}
                                                      alt={badge.name}
                                                      style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                      }}
                                                    />
                                                  ) : (
                                                    <Typography
                                                      variant="caption"
                                                      sx={{
                                                        fontSize: '0.6rem',
                                                        color: '#60a5fa',
                                                        fontWeight: 'bold',
                                                      }}
                                                    >
                                                      {badge.name.charAt(0)}
                                                    </Typography>
                                                  )}
                                                </Box>
                                              </Tooltip>
                                            ),
                                          )}
                                        </Box>
                                      )}
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ color: '#e5e7eb' }}
                              >
                                {baller.held_since}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ color: '#e5e7eb' }}
                              >
                                {baller.amount.toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Paper>
        </Box>
      </Container>

      <ToastContainer
        position="bottom-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss={true}
        draggable={true}
        pauseOnHover={true}
        theme="colored"
      />

      <Snackbar
        open={!!notification}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification?.severity || 'info'}
          variant="filled"
          sx={{
            width: '100%',
            '& .MuiAlert-message': {
              width: '100%',
              textAlign: 'center',
            },
          }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
